import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, BadgeCheck, ShieldOff, ShieldAlert, ShieldCheck, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, StatusBadge, useAdmin } from "@/components/admin/ui";

const STATUS_TABS = [["", "All"], ["active", "Active"], ["suspended", "Suspended"], ["banned", "Banned"]];

export default function AdminUsers() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("users");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    api.adminUsers(q, status).then(setRows).catch(() => setRows([]));
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setStatusFor = async (u, newStatus) => {
    const verb = newStatus === "banned" ? "ban" : newStatus === "suspended" ? "suspend" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${verb} ${u.display_name}?`)) return;
    setBusyId(u.id);
    try { const up = await api.adminUserStatus(u.id, newStatus); setRows((r) => r.map((x) => (x.id === u.id ? { ...x, status: up.status } : x))); toast.success(`${u.display_name} ${verb === "reactivate" ? "reactivated" : verb + "ned"}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusyId(null); }
  };
  const toggleVerify = async (u) => {
    setBusyId(u.id);
    try { const up = await api.adminUserVerify(u.id, !u.verified); setRows((r) => r.map((x) => (x.id === u.id ? { ...x, verified: up.verified } : x))); toast.success(up.verified ? "Verified" : "Verification removed"); }
    catch { toast.error("Failed"); } finally { setBusyId(null); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-users">
      <div><span className={OVERLINE}>Community</span><h1 className={`${H1} mt-1`}>Users</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2 mb-5">Verify hunters, suspend accounts (read-only) or ban them (login blocked). Staff accounts are protected.</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8478]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="w-full border border-[#E5E2DC] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#1F2A22]" data-testid="users-search" />
        </div>
        <div className="flex gap-1.5">
          {STATUS_TABS.map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1.5 text-xs border ${status === k ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`users-filter-${k || "all"}`}>{l}</button>
          ))}
        </div>
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-[#5C6B61] py-10">No users found.</p>
      ) : (
        <div className="border border-[#E5E2DC] bg-white overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#8A8478] border-b border-[#E5E2DC]">
              <th className="px-4 py-2.5">Hunter</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Posts</th><th className="px-4 py-2.5 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-[#F0EEE9] last:border-0" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 font-medium text-[#1F2A22]">{u.display_name}{u.verified && <BadgeCheck size={15} className="text-[#2E7D46]" data-testid={`user-verified-${u.id}`} />}</div>
                    <div className="text-xs text-[#8A8478]">{u.email}{u.is_staff && <span className="ml-2 text-[#8A5A44]">· {(u.roles || []).join(", ")}</span>}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                  <td className="px-4 py-3 text-[#5C6B61]">{u.post_count}</td>
                  <td className="px-4 py-3">
                    {u.is_staff ? (
                      <span className="flex items-center justify-end gap-1 text-xs text-[#8A8478]"><Shield size={13} /> Protected</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button disabled={!canEdit || busyId === u.id} onClick={() => toggleVerify(u)} className={`inline-flex items-center gap-1 text-xs border px-2 py-1 ${u.verified ? "border-[#2E7D46] text-[#2E7D46]" : "border-[#E5E2DC] text-[#5C6B61]"} disabled:opacity-40`} data-testid={`user-verify-${u.id}`}><BadgeCheck size={13} /> {u.verified ? "Verified" : "Verify"}</button>
                        {u.status !== "suspended" && <button disabled={!canEdit || busyId === u.id} onClick={() => setStatusFor(u, "suspended")} className="inline-flex items-center gap-1 text-xs border border-[#8A5A44] text-[#8A5A44] px-2 py-1 disabled:opacity-40" data-testid={`user-suspend-${u.id}`}><ShieldAlert size={13} /> Suspend</button>}
                        {u.status !== "banned" && <button disabled={!canEdit || busyId === u.id} onClick={() => setStatusFor(u, "banned")} className="inline-flex items-center gap-1 text-xs border border-red-600 text-red-600 px-2 py-1 disabled:opacity-40" data-testid={`user-ban-${u.id}`}><ShieldOff size={13} /> Ban</button>}
                        {u.status !== "active" && <button disabled={!canEdit || busyId === u.id} onClick={() => setStatusFor(u, "active")} className="inline-flex items-center gap-1 text-xs border border-[#2E7D46] text-[#2E7D46] px-2 py-1 disabled:opacity-40" data-testid={`user-reactivate-${u.id}`}><ShieldCheck size={13} /> Reactivate</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
